import React, { useRef, useState } from "react";
import { Download, Upload, FileSpreadsheet, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import AdminLayout from "./AdminLayout";

export default function AdminCSV() {
    const fileRef = useRef(null);
    const [seoOnly, setSeoOnly] = useState(false);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const downloadAuthed = async (path, filename) => {
        try {
            const token = localStorage.getItem("ctx_token");
            const resp = await fetch(`${API}${path}`, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Download failed");
        }
    };

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/admin/topics/import/csv?seo_only=${seoOnly}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            toast.success(`Imported: ${data.created} created, ${data.updated} updated`);
        } catch (err) {
            toast.error("Import failed");
        } finally {
            setImporting(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <AdminLayout>
            <div className="p-8 sm:p-10 max-w-4xl">
                <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-[#64748B] hover:text-[#56CCF2] uppercase tracking-wider mb-6">
                    <ArrowLeft className="w-3 h-3" />
                    Dashboard
                </Link>

                <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-2">Editorial</div>
                <h1 className="text-3xl font-bold text-white mb-3">CSV import / export</h1>
                <p className="text-sm text-[#94A3B8] mb-10 max-w-2xl">
                    Bulk-manage topics. Import creates new topics or updates existing ones (matched by slug). SEO-only mode updates only SEO fields without overwriting content — perfect for externally-prepared SEO sheets.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                    <button
                        data-testid="csv-download-template"
                        onClick={() => downloadAuthed("/admin/csv/template", "topics-template.csv")}
                        className="bg-[#111827] border border-[#243044] rounded-xl p-6 text-left hover:border-[#2F80ED] transition-colors group"
                    >
                        <div className="w-10 h-10 rounded-lg bg-[#2F80ED]/15 text-[#56CCF2] flex items-center justify-center mb-4">
                            <FileSpreadsheet className="w-5 h-5" />
                        </div>
                        <h3 className="text-base font-semibold text-white mb-1">Download template</h3>
                        <p className="text-xs text-[#94A3B8]">CSV with all fields and JSON-style sample row.</p>
                    </button>

                    <button
                        data-testid="csv-export-all"
                        onClick={() => downloadAuthed("/admin/topics/export/csv", "context-project-topics.csv")}
                        className="bg-[#111827] border border-[#243044] rounded-xl p-6 text-left hover:border-[#2F80ED] transition-colors group"
                    >
                        <div className="w-10 h-10 rounded-lg bg-[#2F80ED]/15 text-[#56CCF2] flex items-center justify-center mb-4">
                            <Download className="w-5 h-5" />
                        </div>
                        <h3 className="text-base font-semibold text-white mb-1">Export all topics</h3>
                        <p className="text-xs text-[#94A3B8]">Download every topic as CSV (all sections + sources + SEO).</p>
                    </button>
                </div>

                <div className="bg-[#111827] border border-[#243044] rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-[#2F80ED]/15 text-[#56CCF2] flex items-center justify-center">
                            <Upload className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-white">Import topics</h3>
                            <p className="text-xs text-[#94A3B8]">CSV file with slug-keyed rows.</p>
                        </div>
                    </div>

                    <label className="flex items-center gap-3 mb-5 select-none cursor-pointer">
                        <input
                            data-testid="csv-seo-only"
                            type="checkbox"
                            checked={seoOnly}
                            onChange={(e) => setSeoOnly(e.target.checked)}
                            className="w-4 h-4 accent-[#2F80ED]"
                        />
                        <span className="text-sm text-[#CBD5E1]">SEO-only mode (update SEO fields without overwriting content)</span>
                    </label>

                    <input
                        data-testid="csv-file-input"
                        ref={fileRef}
                        type="file"
                        accept=".csv"
                        onChange={onUpload}
                        className="block w-full text-sm text-[#CBD5E1] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#2F80ED] file:text-white hover:file:bg-[#2F80ED]/90 file:cursor-pointer cursor-pointer"
                        disabled={importing}
                    />

                    {importing && <p className="text-sm text-[#56CCF2] mt-4">Importing…</p>}

                    {result && (
                        <div className="mt-5 p-4 bg-[#0B1220] border border-[#243044] rounded-lg" data-testid="csv-result">
                            <div className="flex items-center gap-6 text-sm mb-2">
                                <span className="text-[#56CCF2]"><strong>{result.created}</strong> created</span>
                                <span className="text-white"><strong>{result.updated}</strong> updated</span>
                                {result.errors?.length > 0 && (
                                    <span className="text-red-400"><strong>{result.errors.length}</strong> errors</span>
                                )}
                            </div>
                            {result.errors?.length > 0 && (
                                <ul className="text-xs text-red-300/80 space-y-1 mt-3 max-h-40 overflow-y-auto">
                                    {result.errors.map((err, i) => <li key={i}>· {err}</li>)}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}
